import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Loader2 } from 'lucide-react'
import { useGlobalSearch } from '../hooks/use-global-search'
import { SearchResultGroup } from './search-result-group'
import type { SearchResult, SearchEntityType } from '../types/search.types'
import './search-modal.css'

interface SearchModalProps {
  open: boolean
  onClose: () => void
}

export function SearchModal({ open, onClose }: SearchModalProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState(0)
  const { results, loading } = useGlobalSearch(query)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  // Group results by type
  const groupedResults: Record<SearchEntityType, SearchResult[]> = {
    client: results.filter(r => r.type === 'client'),
    engagement: results.filter(r => r.type === 'engagement'),
    task: results.filter(r => r.type === 'task'),
    document: results.filter(r => r.type === 'document'),
  }

  // Focus input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      inputRef.current.focus()
    }
  }, [open])

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setQuery('')
      setSelectedIndex(0)
    }
  }, [open])

  // Keyboard navigation
  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      } else if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(prev => Math.min(prev + 1, results.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(prev => Math.max(prev - 1, 0))
      } else if (e.key === 'Enter') {
        e.preventDefault()
        const selectedResult = results[selectedIndex]
        if (selectedResult) {
          handleSelect(selectedResult)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, results, selectedIndex, onClose])

  const handleSelect = (result: SearchResult) => {
    navigate(result.path)
    onClose()
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }

  if (!open) return null

  return (
    <div className="search-overlay" onClick={handleOverlayClick}>
      <div className="search-modal">
        <div className="search-input-wrapper">
          <Search size={20} style={{ color: 'var(--text-tertiary)' }} />
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            placeholder="Type to search... (Ctrl+K)"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setSelectedIndex(0)
            }}
          />
          {loading && <Loader2 size={20} className="spinner" />}
        </div>

        <div className="search-results">
          {!query.trim() && (
            <div className="search-empty">
              Type to search across clients, engagements, tasks, and documents
            </div>
          )}

          {query.trim() && results.length === 0 && !loading && (
            <div className="search-empty">No results found</div>
          )}

          {results.length > 0 && (
            <>
              {(['client', 'engagement', 'task', 'document'] as SearchEntityType[]).map((type) => {
                const typeResults = groupedResults[type]
                const baseIndex = results.findIndex(r => r.type === type)

                return (
                  <SearchResultGroup
                    key={type}
                    type={type}
                    results={typeResults}
                    selectedIndex={selectedIndex}
                    baseIndex={baseIndex === -1 ? 0 : baseIndex}
                    onSelect={handleSelect}
                  />
                )
              })}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
